import { useState } from 'react'

import { Collapse, Modal, NumberInput, Select, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { CREATE_DATA_MIGRATION } from 'src/graphql/dataMigrations.graphql'
import { MigrationProvider } from 'src/graphql/types/dataMigrations'
import IconChevronDown from 'src/icons/IconChevronDown'
import { PROVIDER_LABELS } from 'src/pages/SettingsPage/tabs/dataMigrationTab/lib/migrationDisplay'

const DEFAULT_BATCH_SIZE = 200
const DEFAULT_PARALLEL = 20

/**
 * TenStreet only for now. The API and the pipeline support DriverReach and CSV
 * as well — add them back here when we actually run one, so the form only ever
 * offers a path we've exercised.
 */
const PROVIDER_OPTIONS = [
  { value: MigrationProvider.TENSTREET, label: PROVIDER_LABELS.TENSTREET },
]

interface NewMigrationModalProps {
  opened: boolean
  onClose: () => void
  onCreated: () => void
}

const NewMigrationModal = ({
  opened,
  onClose,
  onCreated,
}: NewMigrationModalProps) => {
  const [advancedOpened, setAdvancedOpened] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const form = useForm({
    initialValues: {
      provider: MigrationProvider.TENSTREET as string,
      sourceUrl: '',
      sourceZipPassword: '',
      batchSize: DEFAULT_BATCH_SIZE,
      parallel: DEFAULT_PARALLEL,
    },
    validate: {
      provider: (value) => (value ? null : 'Provider is required'),
      sourceUrl: (value) => {
        const trimmed = value.trim()
        if (!trimmed) return 'Download link is required'
        if (!trimmed.startsWith('https://')) {
          return 'Download link must be an https:// URL'
        }
        return null
      },
      sourceZipPassword: (value) => (value ? null : 'Zip password is required'),
      batchSize: (value) =>
        typeof value === 'number' && value >= 1 && value <= 1000
          ? null
          : 'Batch size must be between 1 and 1000',
      parallel: (value) =>
        typeof value === 'number' && value >= 1 && value <= 100
          ? null
          : 'Parallel must be between 1 and 100',
    },
  })

  const [createDataMigration] = useMutation(CREATE_DATA_MIGRATION)

  const handleClose = () => {
    if (submitting) return
    form.reset()
    setAdvancedOpened(false)
    onClose()
  }

  const handleSubmit = async (values: typeof form.values) => {
    setSubmitting(true)
    try {
      // migrationId is deliberately not collected — the server generates
      // <Company_Name>_<YYYY-MM-DD>, matching the runbook convention.
      const { data } = await createDataMigration({
        variables: {
          input: {
            provider: values.provider,
            sourceUrl: values.sourceUrl.trim(),
            sourceZipPassword: values.sourceZipPassword,
            batchSize: values.batchSize,
            parallel: values.parallel,
          },
        },
      })
      if (!data?.createDataMigration?.dataMigrationId) {
        throw new Error('Migration was not created, please try again.')
      }

      toast('Migration started successfully', 'success')
      form.reset()
      setAdvancedOpened(false)
      onCreated()
      onClose()
    } catch (error) {
      toast((error as Error)?.message || 'Unable to start migration', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Migration"
      closeOnEscape
      closeOnClickOutside
      classNames={{
        title: 'font-semibold',
        body: 'flex flex-col gap-4',
      }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        className="flex flex-col gap-4"
      >
        <Select
          label="Provider"
          data={PROVIDER_OPTIONS}
          allowDeselect={false}
          checkIconPosition="right"
          classNames={{
            label: 'text-sm font-medium text-doubleNickel-gray-700',
          }}
          {...form.getInputProps('provider')}
        />

        <TextInput
          label="Download link"
          placeholder="https://example.com/export.zip"
          required
          classNames={{
            label: 'text-sm font-medium text-doubleNickel-gray-700',
          }}
          {...form.getInputProps('sourceUrl')}
        />

        <TextInput
          label="Zip password"
          placeholder="Password for the export zip"
          type="password"
          required
          classNames={{
            label: 'text-sm font-medium text-doubleNickel-gray-700',
          }}
          {...form.getInputProps('sourceZipPassword')}
        />

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setAdvancedOpened((prev) => !prev)}
            aria-expanded={advancedOpened}
            className="flex w-fit flex-row items-center gap-1 text-xs font-medium text-doubleNickel-gray-600 hover:text-doubleNickel-gray-800"
          >
            <IconChevronDown
              className={`h-3.5 w-3.5 ${
                !advancedOpened && '-rotate-90'
              } fill-none stroke-current transition-all`}
            />
            Advanced
          </button>
          <Collapse in={advancedOpened}>
            <div className="flex flex-row gap-3">
              <NumberInput
                label="Batch size"
                min={1}
                max={1000}
                classNames={{
                  label: 'text-sm font-medium text-doubleNickel-gray-700',
                }}
                {...form.getInputProps('batchSize')}
              />
              <NumberInput
                label="Parallel"
                min={1}
                max={100}
                classNames={{
                  label: 'text-sm font-medium text-doubleNickel-gray-700',
                }}
                {...form.getInputProps('parallel')}
              />
            </div>
          </Collapse>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <DNButton variant="outline" type="button" onClick={handleClose}>
            Cancel
          </DNButton>
          <DNButton variant="primary" type="submit" loading={submitting}>
            Start Migration
          </DNButton>
        </div>
      </form>
    </Modal>
  )
}

export default NewMigrationModal
